import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useAuth } from '@/shared/hooks/useAuth';
import { Spinner } from '@/shared/ui';

import styles from './guards.module.css';

function Waiting(): React.ReactElement {
  return (
    <div className={styles.waiting}>
      <Spinner size={28} />
    </div>
  );
}

/**
 * Пока статус 'loading', редиректить нельзя: в TMA автологин занимает
 * сотни миллисекунд, и мгновенный переход на /auth дал бы вспышку экрана
 * входа там, где он не должен показываться вообще (D-09).
 *
 * Клиентский кабинет доступен ВСЕМ авторизованным, включая admin/owner:
 * у управляющего есть свой капитал и своя история, он такой же участник.
 * Переход в админку — через отдельную вкладку навигации.
 *
 * Редирект только через <Navigate>, не через window.location: полная
 * перезагрузка страницы сбрасывает состояние и запускает bootstrap заново.
 */
export function RequireAuth({ children }: { children: ReactNode }): React.ReactElement {
  const { status, me } = useAuth();
  const location = useLocation();

  if (status === 'loading') return <Waiting />;

  if (status === 'anon' || !me) {
    return <Navigate to="/auth" replace state={{ from: location.pathname }} />;
  }

  return <>{children}</>;
}

/** Кабинеты «Админ» и «Процесс»: роли admin и owner (D-08). Client редиректятся на /. */
export function RequireStaff({ children }: { children: ReactNode }): React.ReactElement {
  const { status, isStaff, me } = useAuth();
  const location = useLocation();

  if (status === 'loading') return <Waiting />;

  if (status === 'anon' || !me) {
    return <Navigate to="/auth" replace state={{ from: location.pathname }} />;
  }

  // Client не может на админские экраны
  if (!isStaff) return <Navigate to="/" replace />;
  return <>{children}</>;
}

/** Настройки и выдача доступов: только owner (D-08). */
export function RequireOwner({ children }: { children: ReactNode }): React.ReactElement {
  const { status, isOwner, isStaff, me } = useAuth();
  const location = useLocation();

  if (status === 'loading') return <Waiting />;

  if (status === 'anon' || !me) {
    return <Navigate to="/auth" replace state={{ from: location.pathname }} />;
  }

  if (!isOwner) return <Navigate to={isStaff ? '/admin' : '/'} replace />;
  return <>{children}</>;
}
